import { logger } from 'lib/logger';
import { BASEQUOTA, IS_PUBLIC } from 'reactApp/appHelpers/configHelpers';
import {
    editingDocumentRestrictionsC,
    editingDocumentRestrictionsQuota,
    isEditingRestrictedABEnabled,
} from 'reactApp/appHelpers/featuresHelpers/features/editingDocumentRestrictions';
import { loadSubscriptionProducts } from 'reactApp/modules/billingData/billingData.provider';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { hasMonthTariff } from 'reactApp/modules/products/products.helpers';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { EProductPeriod } from 'reactApp/modules/products/products.types';
import { isOwnPublic } from 'reactApp/modules/public/public.selectors';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { loadUserQuotaSuccess } from 'reactApp/modules/userQuota/userQuota.module';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { store } from 'reactApp/store';
import { PERIODS } from 'reactApp/utils/Period';
import { captureException } from 'reactApp/utils/tracer';
import { call, cancel, put, select, take, takeEvery } from 'redux-saga/effects';

import { EditorRestrictionsActions } from './editorRestrictions.module';
import { EditorRestrictionsSelectors } from './editorRestrictions.selectors';

export function* handleEditorRestrictions(action: ReturnType<typeof EditorRestrictionsActions.checkEditingDisabled>) {
    const { isLoaded: isUserQuotaLoaded } = yield select(UserQuotaSelectors.getLoadingState);
    if (!isUserQuotaLoaded) {
        yield take(loadUserQuotaSuccess);
    }

    const isPaid = yield select(UserSelectors.isPaidUser);
    const isCorp = yield select(UserSelectors.isCorpUser);
    const totalQuota: number = yield select(UserQuotaSelectors.getTotalOrBase);
    const baseQuota: number = yield select(UserSelectors.getBasicSpace) || BASEQUOTA;
    const userQuota = totalQuota - baseQuota;
    const isUserWithBilling = yield select(UserSelectors.isUserWithBilling);
    const isOwnerPublic = yield select(isOwnPublic);
    const isOwner = IS_PUBLIC ? isOwnerPublic : true;

    if (!action.payload.isR7 || !isPaid || !isUserWithBilling || !isEditingRestrictedABEnabled || isCorp || !isOwner) {
        yield put(EditorRestrictionsActions.setEditingDisabled({ editingRestricted: false }));
        yield cancel();
    }

    const diff = userQuota - editingDocumentRestrictionsQuota;
    const violates = diff < 0;

    yield put(EditorRestrictionsActions.setEditingDisabled({ editingRestricted: violates, quotaShortfall: Math.abs(diff) }));
}

export function* handleSuggestTariff(action: ReturnType<typeof EditorRestrictionsActions.suggestTariff>) {
    const violates = yield select(EditorRestrictionsSelectors.isEditingRestricted);

    try {
        if (violates) {
            const product = yield call(suggestProductHelper);

            if (!product) {
                yield put(
                    showSnackbarAction({
                        type: SnackbarTypes.failure,
                        id: 'editor_restrictions',
                        text: 'Не удалось подобрать тариф',
                        closable: true,
                    })
                );
                yield cancel();
            }

            const isMobile = EnvironmentSelectors.isMobile();

            yield put(
                openTariffBuy({
                    productId: product.id,
                    paySource: action.payload.paysource,
                    isMobile,
                    successMessage: 'Подписка активирована — теперь можете редактировать документы',
                    onSuccess: () => {
                        store.dispatch(EditorRestrictionsActions.setEditingEnabled());
                    },
                })
            );
        }
    } catch (error) {
        yield put(
            showSnackbarAction({
                type: SnackbarTypes.failure,
                id: 'editor_restrictions',
                text: 'Не удалось подобрать тариф',
                closable: true,
            })
        );
        captureException(error, { issueKey: 'editorRestrictions/error' });
        logger.error(error);
    }
}

function* suggestProductHelper() {
    const quotaShortfall = yield select(EditorRestrictionsSelectors.quotaShortfall);
    const hasTrial = editingDocumentRestrictionsC;

    const subsTariffs = yield loadSubscriptionProducts();

    const periodBySubs = hasMonthTariff(subsTariffs) ? PERIODS.month : PERIODS.year;
    const { product } = yield select(
        ProductsSelectors.getSpecificProductByQuota,
        quotaShortfall - 1,
        hasTrial ? EProductPeriod.month : periodBySubs,
        hasTrial
    );

    return product;
}

export function* watchEditorRestrictions() {
    yield takeEvery(EditorRestrictionsActions.checkEditingDisabled.toString(), handleEditorRestrictions);
    yield takeEvery(EditorRestrictionsActions.suggestTariff.toString(), handleSuggestTariff);
}
